<script setup>
  import { onUnmounted, ref } from "vue";
  import { useFetch } from "@/composables/useFetch.js";
  import { uri } from "@/constants/config.js";
  import "@bhplugin/vue3-datatable/dist/style.css";
  import { useRoute, useRouter } from "vue-router";
  import BasePagination from "@/base-components/Table/BasePagination.vue";
  import Lucide from "@/base-components/Lucide";
  import { useI18n } from "vue-i18n";
  import InstanceTables from "@/components/Tables/InstanceTables.vue";
  import ConfirmationDialog from "@/base-components/Dialog/ConfirmationDialog.vue";
  import { toast } from "vue3-toastify";

  const { t: $t } = useI18n();
  const route = useRoute();
  const router = useRouter();
  const user = ref({});
  const vmList = ref([]);
  const cols = ref([
    { field: "counter", title: "#" },
    { field: "id", title: $t("title.id") },
    { field: "country", title: $t("title.regions") },
    { field: "name", title: $t("title.server-name") },
    { field: "hostname", title: $t("title.hostname") },
    { field: "Interfaces", title: $t("title.ip") },
    { field: "Volumes", title: $t("title.volume") },
    { field: "InstanceStatus", title: $t("title.instance-status") },
    { field: "os", title: $t("title.os-type") },
    { field: "ServiceStatus", title: $t("title.service-status") },
  ]);
  const pageOptions = ref({
    count: 1,
    limit: 10,
    page: 1,
  });
  const loadingFlag = ref(false);
  let timer = null;
  let useTimer = true;
  async function getVMList(showLoading = true) {
    loadingFlag.value = showLoading;

    try {
      // Await the result of useFetch
      const { data, error } = await useFetch(
        `${uri.api.iaas.instance.list}?page=${pageOptions.value.page}`,
        { method: "GET" }
      );
      if (!error) {
        vmList.value = data.data;
        pageOptions.value.count = data.meta.total;
        pageOptions.value.limit = data.meta.perPage;
        if (
          data.data.filter(
            (x) =>
              x.status === 2 ||
              x.powerStatus === 0 ||
              (x.buildStatus < 2 && x.status === 5)
          ).length &&
          useTimer
        ) {
          timer = setTimeout(() => getVMList(false), 1000);
        }
      }
    } catch (err) {
      timer = setTimeout(getVMList, 2000);
      console.error(err);
    } finally {
      loadingFlag.value = false;
    }
  }

  const showActiveRequestDialog = ref(false);
  const selectedInstance = ref(null);
  function rowClick(instance) {
    if (instance.status === 5 && instance.buildStatus === 2)
      router.push(`/vm/${instance.id}`);

    if (instance.status === 10) {
      selectedInstance.value = instance;
      showActiveRequestDialog.value = true;
    }
  }

  async function activeRequest() {
    showActiveRequestDialog.value = false;
    if (!selectedInstance.value) return;

    await useFetch(
      uri.api.iaas.instance.sub(selectedInstance.value.id, "active"),
      { method: "POST" }
    );
    toast("درخواست فعال سازی سرور ثبت شد", {
      theme: "auto",
      type: "success",
      dangerouslyHTMLString: true,
    });
    getVMList(true);
    selectedInstance.value = null;
  }

  getVMList();

  onUnmounted(() => {
    useTimer = false;
    clearTimeout(timer);
  });
</script>

<template>
  <section>
    <ConfirmationDialog
      v-if="selectedInstance && showActiveRequestDialog"
      :close="() => (showActiveRequestDialog = false)"
      :show="showActiveRequestDialog"
      :accept="activeRequest"
      :deny="() => (showActiveRequestDialog = false)"
    >
      <p class="p-5 m-5">
        {{ $t("message.are-you-sure-active-instance") }}:
        {{ selectedInstance.name }}
      </p>
    </ConfirmationDialog>
    <div class="grid grid-cols-12 gap-6 mt-8">
      <div class="col-span-12 lg:col-span-12 2xl:col-span-12">
        <!-- BEGIN: Inbox Filter -->
        <div class="flex flex-col-reverse items-center intro-y sm:flex-row">
          <div class="relative w-full mt-3 mr-auto sm:w-auto sm:mt-0"></div>
          <div class="flex w-full sm:w-auto gap-2"></div>
        </div>
        <!-- END: Inbox Filter -->
        <!-- BEGIN: Inbox Content -->
        <div class="mt-5 intro-y box">
          <div
            class="flex p-5 border-b sm:flex-row text-slate-500 border-slate-200/60 text-end w-full justify-end items-end"
          >
            <div class="w-5 h-5 ms-5 cursor-pointer" @click="getVMList">
              <Lucide icon="RefreshCw" class="w-6 h-6" />
            </div>
            <div
              class="w-5 h-5 ms-5 cursor-pointer"
              @click="router.push('/vm/create')"
            >
              <Lucide icon="PlusCircle" class="w-6 h-6" />
            </div>
          </div>
          <div class="mb-5 relative"></div>
          <InstanceTables
            :rows="vmList"
            :loading="loadingFlag"
            :page-options="pageOptions"
            @row-click="rowClick"
            admin-mode="user"
          ></InstanceTables>
          <div
            class="flex flex-col items-center p-5 text-center sm:flex-row sm:text-left text-slate-500"
          >
            <div>
              <BasePagination
                v-if="pageOptions.count > pageOptions.limit"
                :page-options="pageOptions"
                @page-change="getVMList"
              />
            </div>
            <div class="mt-2 sm:ml-auto sm:mt-0"></div>
          </div>
        </div>
        <!-- END: Inbox Content -->
      </div>
    </div>
  </section>
</template>

<style scoped></style>
